import type {
  QuizSingleStep,
  QuizSteps,
  QuizAnswerValue,
} from "@/types/api.types";
import QuizTitle from "@/components/atoms/QuizTitle";
import PrimaryButton from "@/components/atoms/PrimaryButton";
import Graph from "@/components/atoms/Graph";
import StickyAnswerCta from "@/components/atoms/StickyAnswerCta";
import { useStickyLastOption } from "@/hooks/useStickyLastOption";
import { useQuizData } from "@/hooks/useQuizData";
import parse from "html-react-parser";

type QuizOption = NonNullable<QuizSingleStep["options"]>[number];

const NO_OPTIONS: QuizOption[] = [];

const unitLabelMap: Record<string, string> = {
  metric: "kg",
  imperial: "lb",
};

const formatWeight = (v?: QuizAnswerValue) => {
  if (!v) return "";

  if (typeof v === "object" && "value" in v && "unit" in v) {
    const value = String(v.value);
    const unit = unitLabelMap[v.unit] ?? String(v.unit);
    return `${value} ${unit}`.trim();
  }

  return String(v);
};

const InfoStep = ({ step, onAnswer, answers = {} }: QuizSteps) => {
  const isGraphStep = step.id === "graph";
  const currentWeight = formatWeight(answers["weight_current"]);
  const goalWeight = formatWeight(answers["weight_goal"]);
  const { data } = useQuizData();
  const stickySettings = data?.settings?.stickyLastAnswer;
  const { listRef, stickyOption } = useStickyLastOption(
    stickySettings?.enabled ? (step.options ?? NO_OPTIONS) : NO_OPTIONS,
  );

  return (
    <div className="mx-auto w-full">
      <QuizTitle title={step.title} className="pb-6" />
      {isGraphStep ? (
        <Graph
          currentWeight={currentWeight || "—"}
          goalWeight={goalWeight || "—"}
          gradientEnd="var(--bg-light)"
          step={step}
        />
      ) : (
        step.imageUrl && (
          <div className="mb-6 overflow-hidden">
            <img
              src={step.imageUrl}
              alt="graph"
              className="mx-auto h-auto w-full object-contain"
            />
          </div>
        )
      )}

      {step.subtitle && (
        <div className="mb-8 text-[16px] leading-[1.5] font-normal text-black mt-6">
          {parse(step.subtitle)}
        </div>
      )}

      <div ref={listRef} className="flex flex-col gap-3">
        {step.options?.map((option) => (
          <PrimaryButton
            key={option.id}
            label={option.label}
            onClick={() => onAnswer(option.value)}
          />
        ))}
      </div>

      {stickyOption && (
        <StickyAnswerCta
          label={stickyOption.label}
          onClick={() => onAnswer(stickyOption.value)}
          settings={stickySettings}
        />
      )}
    </div>
  );
};

export default InfoStep;
